import { Component, ElementRef, signal, ViewChild, AfterViewChecked, ChangeDetectorRef, DestroyRef, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { DateRange, MatDatepickerModule, MatDateRangePicker } from '@angular/material/datepicker';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BookingService } from '../../../../services/booking.service';
import { MatChipsModule } from "@angular/material/chips";
import { animate, style, transition, trigger } from '@angular/animations';
import { SnackBarService } from '../../../../services/snackbar.service';
import { AuthService } from '../../../../../core/services/auth.service';
import { PropertyService } from '../../../../../features/property/services/property.service';
import { debounceTime, distinctUntilChanged, filter, mergeMap, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { User } from '../../../../user/models/user.model';
import { RecaptchaService } from '../../../../../core/services/recaptcha.service';
import { BookingRequest } from '../../../models/booking-request.model';

export interface ChatMessage {
    text: string;
    sender: 'system' | 'user';
}

@Component({
    selector: 'app-booking-chat',
    standalone: true,
    providers: [provideNativeDateAdapter()],
    imports: [CommonModule, MatButtonModule, MatIconModule, MatDatepickerModule, MatChipsModule],
    templateUrl: './booking-chat.component.html',
    styleUrl: './booking-chat.component.scss',
    animations: [
        trigger('bubbleIn', [
            transition(':enter', [
                style({ opacity: 0, transform: 'scale(0.8) translateY(20px)' }),
                animate('200ms ease-out', style({ opacity: 1, transform: 'scale(1) translateY(0)' }))
            ])
        ])
    ]
})
export class BookingChatComponent implements AfterViewChecked, OnInit {
    private bookingService = inject(BookingService);
    private auth = inject(AuthService);
    private propertyService = inject(PropertyService);
    private cdr = inject(ChangeDetectorRef);
    private dialogRef = inject<MatDialogRef<BookingChatComponent>>(MatDialogRef);
    private snackbar = inject(SnackBarService);
    private recaptchaService = inject(RecaptchaService);
    private destroyRef = inject(DestroyRef);
    data = inject(MAT_DIALOG_DATA);

    @ViewChild('scrollFrame') private scrollContainer!: ElementRef;
    @ViewChild('picker') private picker!: MatDateRangePicker<Date>;
    propertyId: string | null = null;
    user: User | null = null;
    loading = false;
    submitted = false;
    minDate: Date = new Date();
    propertyOwnerEmail: string | null = null;
    contactPreferenceMap: Record<string, 'email' | 'phone' | 'text'> = {
        'email': 'email',
        'call': 'phone', // Maps "Call" button to 'phone'
        'text': 'text'
    };

    messages = signal<ChatMessage[]>([
        { text: "Hey! I'm here to help get you started with your stay. When are you looking to visit?", sender: 'system' }
    ]);

    bookingData = {
        startDate: null as Date | null,
        endDate: null as Date | null,
        contactMethod: '',
        phone: '',
        email: '',
        message: '',
        propertyId: ''
    };

    currentStep = 0;
    isTyping = signal(false);

    constructor() {
        const data = this.data;

        this.bookingData.propertyId = data.propertyId;
        this.propertyId = data.propertyId;
    }

    ngOnInit() {
        this.auth.user$.pipe(
            filter(user => user !== undefined),
            debounceTime(0),
            distinctUntilChanged(),
            tap(user => {
                this.loading = false;

                if (user) {
                    this.user = user;
                }
                this.cdr.detectChanges();
            }),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe();

        if (this.propertyId) {
            this.propertyService.getPropertyOwnerByPropertyId(this.propertyId).pipe(tap(owner => {
                this.propertyOwnerEmail = owner.username;
            })).subscribe();
        }
    }

    ngAfterViewChecked() { this.scrollToBottom(); }

    private scrollToBottom(): void {
        setTimeout(() => {
            try {
                const element = this.scrollContainer.nativeElement;
                element.scrollTo({
                    top: element.scrollHeight,
                    behavior: 'smooth'
                });
            } catch (err) { }
        }, 50);
    }

    onPickerClosed() {
        const selection = (this.picker as any)._model.selection as DateRange<Date>;
        const start = selection?.start;
        const end = selection?.end;

        if (start && end) {
            this.bookingData.startDate = start;
            this.bookingData.endDate = end;

            const label = `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
            this.addReply(label, "Great! Do you prefer we contact you via call, text, or email?");
        }
    }

    onMethodSelected(method: string) {
        const chosenMethod = method.toLocaleLowerCase();
        this.bookingData.contactMethod = method.toLocaleLowerCase();
        const camelKey = this.toCamelCase(method) as keyof typeof this.bookingData;
        if (chosenMethod == 'email') {
            this.addReply(method, "Perfect. What's the best email for the owner to reach you at?", camelKey);
        } else {
            this.addReply(method, "Perfect. What's the best number for the owner to reach you at?", camelKey);
        }
    }

    toCamelCase(str: string): string {
        return str
            .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) =>
                index === 0 ? word.toLowerCase() : word.toUpperCase()
            )
            .replace(/\s+/g, '');
    }
    async addReply(userResponse: string, nextQuestion?: string, key?: keyof typeof this.bookingData) {
        if (!userResponse.trim()) return;

        if (key && key in this.bookingData) {
            (this.bookingData as any)[key] = userResponse;
        }

        this.messages.update(prev => [...prev, { text: userResponse, sender: 'user' }]);

        this.isTyping.set(true);
        await new Promise(resolve => setTimeout(resolve, 1000));
        this.isTyping.set(false);

        if (nextQuestion) {
            this.messages.update(prev => [...prev, { text: nextQuestion, sender: 'system' }]);
            this.currentStep++;
        } else {
            this.submitBooking();
        }
    }

    async submitBooking() {
        this.isTyping.set(true);
        this.currentStep = 4;
        console.log(this.propertyId, this.user, this.propertyOwnerEmail);

        this.recaptchaService.execute('booking_request_form_submission').pipe(
            mergeMap(token => {
                console.log(this.user, this.propertyOwnerEmail);
                const email = this.bookingData.email.length ? this.bookingData.email : this.user?.username;
                const propertyOwner = this.propertyOwnerEmail?.length ? this.propertyOwnerEmail : 'support@horizinns.com';
                const start = this.bookingData.startDate ?? new Date();
                const end = this.bookingData.endDate ?? new Date();
                const propertyId = this.propertyId ?? '';
                console.log('propertyId', propertyId);

                const request: BookingRequest = {
                    submitterId: this.user?.id ?? '',
                    itemId: propertyId,
                    firstName: this.user?.firstName ?? '',
                    lastName: this.user?.lastName ?? '',
                    startDate: this.formatDate(start),
                    endDate: this.formatDate(end),
                    email: email ?? '',
                    phone: this.bookingData.phone ?? '',
                    contactPreference: this.contactPreferenceMap[this.bookingData.contactMethod.toLocaleLowerCase()],
                    propertyContactEmail: propertyOwner,
                    message: this.bookingData.message,
                    recaptchaToken: token
                };
                console.log('booking request', request, this.user, this.propertyId);
                return this.bookingService.submitBookingRequest(request);
            }),
            tap({
                next: (res) => {
                    console.log('Horizinns Inquiry Success!', res);
                    this.isTyping.set(false);
                    this.messages.update(prev => [...prev, {
                        text: "Your request has been sent! The owner will be in touch soon.",
                        sender: 'system'
                    }]);

                    this.scrollToBottom();

                    setTimeout(() => this.dialogRef.close(), 8000);
                },
                error: (err) => {
                    console.error('Horizinns Inquiry Error:', err);
                    let msg = undefined;
                    if (err.status == 401) {
                        msg = "You must be signed in to submit an inquiry";
                    }
                    this.cdr.detectChanges();
                    this.snackbar.open(msg ? msg : 'There was an error processing your request.', 'Close', {
                        duration: 4000,
                        panelClass: ['error-snackbar']
                    });
                    this.isTyping.set(false);
                    this.currentStep = 3;
                    this.messages.update(prev => [...prev, { text: "Sorry, I had trouble sending that.", sender: 'system' }]);
                    this.scrollToBottom();
                }
            })
        ).subscribe();
    }

    formatPhoneNumber(value: string): string {
        if (!value) return value;
        const phoneNumber = value.replace(/[^\d]/g, '');
        const trimmed = phoneNumber.substring(0, 10);
        if (trimmed.length < 4) return trimmed;
        if (trimmed.length < 7) return `(${trimmed.slice(0, 3)}) ${trimmed.slice(3)}`;
        return `(${trimmed.slice(0, 3)}) ${trimmed.slice(3, 6)}-${trimmed.slice(6, 10)}`;
    }

    private formatDate(date: Date): string {
        return date.toISOString().split('T')[0];
    }
}
